const CACHE_PREFIX = "help-cache:v1:";
const TTL_MS = 5 * 60 * 1000;

type CachedEntry<T> = {
  storedAt: number;
  payload: T;
};

export function getHelpCacheScope(userId: number, scope: string) {
  return `${userId}:${scope}`;
}

function cacheKey(userId: number, scope: string) {
  return `${CACHE_PREFIX}${getHelpCacheScope(userId, scope)}`;
}

export function readHelpCache<T>(userId: number, scope: string): T | null {
  try {
    const raw = localStorage.getItem(cacheKey(userId, scope));
    if (!raw) return null;
    const entry = JSON.parse(raw) as CachedEntry<T>;
    if (Date.now() - entry.storedAt > TTL_MS) {
      localStorage.removeItem(cacheKey(userId, scope));
      return null;
    }
    return entry.payload;
  } catch {
    return null;
  }
}

/** Stale-while-revalidate fallback when the help API is slow or unreachable. */
export function readHelpCacheStale<T>(userId: number, scope: string): T | null {
  try {
    const raw = localStorage.getItem(cacheKey(userId, scope));
    if (!raw) return null;
    const entry = JSON.parse(raw) as CachedEntry<T>;
    return entry.payload;
  } catch {
    return null;
  }
}

export function writeHelpCache<T>(userId: number, scope: string, payload: T) {
  try {
    const entry: CachedEntry<T> = {storedAt: Date.now(), payload};
    localStorage.setItem(cacheKey(userId, scope), JSON.stringify(entry));
  } catch {
    // quota / private mode
  }
}

export function purgeHelpCacheForUser(userId: number) {
  try {
    const prefix = `${CACHE_PREFIX}${userId}:`;
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch {
    // ignore
  }
}

export const HELP_STALE_TIME_MS = TTL_MS;
